import React, {useEffect, useMemo, useState} from 'react';
import {injectIntl} from 'react-intl';
import {Redirect, Route, Switch, useHistory, useLocation, useRouteMatch, withRouter} from 'react-router-dom';
import {createFragmentContainer, graphql} from 'react-relay';
import {cloneDeep} from 'lodash';
import PhasesSection from './PhasesSection';
import {ContentContainer, HeaderBarStyled, InitialPlanPageWrapper, StickySection} from './InitialPlan.styled';
import Util from '../../forecast-app/shared/util/util';
import * as tracking from '../../tracking';
import HeaderBar, {buildThirdLevelNavHeader} from '../../forecast-app/shared/components/headers/header-bar/header_bar';
import {BUDGET_TYPE, BUTTON_COLOR, BUTTON_STYLE, ELEMENT_TYPE} from '../../constants';
import {getInitialOptions, handleChangedOptions, theEyeToColumns} from '../../the_eye_util';
import {setShouldCollapseAll, theEyeOptions, toggleAllPhases} from './InitialPlanUtil';
import {adjustBaseline, createNewPhase} from './InitialPlanLogic';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {Settings} from './Settings';
import EmptyState from '../../forecast-app/shared/components/empty-states/empty_state';
import {CustomScrollDiv} from 'web-components';
import {withSocketHandling} from '../../socket/withSocketHandling';
import {getSocketConfig} from './InitialPlanSocket';
import HeaderSection from './HeaderSection';
import {dispatch, EVENT_ID} from '../../containers/event_manager';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import PhaseFormatter from '../../forecast-app/shared/util/export-formatters/PhaseFormatter';
import {useDataExport} from '../../forecast-app/shared/hooks/useDataExport';

export const dataExportQuery = graphql`
	query InitialPlanPage_exportQuery($projectId: ID) {
		viewer {
			project(internalId: $projectId) {
				id
				name
				phases(first: 1000000) @connection(key: "Project_phases", filters: []) {
					edges {
						node {
							id
							name
							baselineStartYear
							baselineStartMonth
							baselineStartDay
							baselineDeadlineYear
							baselineDeadlineMonth
							baselineDeadlineDay
							phaseBaselineRoles(first: 100000) @connection(key: "Phase_phaseBaselineRoles", filters: []) {
								edges {
									node {
										id
										role {
											id
											name
										}
										baselineMinutes
										baselinePrice
										baselineRatePerHour
										baselineCost
										baselineCostPerHour
										baselineProfit
										baselinePrice
									}
								}
							}
							phaseBaselineExpenses(first: 100000) @connection(key: "Phase_phaseBaselineExpenses", filters: []) {
								edges {
									node {
										id
										phaseId
										expenseCategory {
											id
											name
										}
										expenseCost
										expenseMarkup
										expenseRevenue
										expenseProfit
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

export const InitialPlanPage = ({intl, viewer, retry, setSocketConfig, buyNowTime}) => {
	const projectDoneOrHalted = viewer.project.status === 'DONE' || viewer.project.status === 'HALTED';
	const SETTINGS_PATH = 'settings';
	const {path} = useRouteMatch();
	const location = useLocation();
	const history = useHistory();
	const localStorageTheEyeName = 'the-eye-baseline';
	const currencySymbol = Util.GetCurrencySymbol(viewer.project.rateCard ? viewer.project.rateCard.currency : viewer.company.currency);
	const [theEyeOpts, setTheEyeOptions] = useState(getInitialOptions(theEyeOptions(viewer.project.budgetType === BUDGET_TYPE.NON_BILLABLE), localStorageTheEyeName));
	const enabledColumns = useMemo(() => theEyeToColumns(theEyeOpts), [theEyeOpts]);
	const [collapseAll, setCollapseAll] = useState(true);
	const useThirdLevel = viewer.availableFeatureFlags.some(flag => flag.key === 'third_level_nav');

	const [phaseSortOrder, setPhaseSortOrder] = useState(Util.sortBaselinePhase(cloneDeep(viewer.project.phases.edges)).map(phase => phase.node.id));

	const phaseFormatter = new PhaseFormatter(intl);
	const exportData = useDataExport(
		phaseFormatter,
		dataExportQuery,
		data => data.viewer.project.phases.edges.map(edge => edge.node),
		data => `${data.viewer.project.name}_baseline`
	);

	useEffect(() => {
		const name = viewer.project ? (viewer.project.name !== null && viewer.project.name !== '' ? viewer.project.name : 'P-' + viewer.project.companyProjectId) : null;
		document.title = 'Baseline - ' + name + ' - Forecast';
		tracking.trackPage('Baseline');
		tracking.trackEyeSelector(theEyeOpts);

		const projectId = parseInt(atob(viewer.project.id).replace('ProjectType:', ''));
		setSocketConfig(getSocketConfig(projectId));
		setShouldCollapseAll(viewer.project.id, collapseAll, setCollapseAll);
		dispatch(EVENT_ID.PAGE_LOADED, 'project_initial_plan');
	}, []);

	// Redirect if not a baseline project
	if (!Util.isBaselineProject(viewer.company.modules, viewer.project)) {
		return <Redirect to={'/project/P-' + viewer.project.companyProjectId + '/workflow/'} />;
	}

	const handleTheEyeOptionSelect = (_, __, ___, newOptions) => {
		const optionOrder = theEyeOptions(viewer.project.budgetType === BUDGET_TYPE.NON_BILLABLE).map(opt => opt.name);
		const sortedNewOptions = newOptions.sort((a, b) => (optionOrder.indexOf(a.name) > optionOrder.indexOf(b.name) ? 1 : -1));
		tracking.trackEyeSelector(sortedNewOptions);
		setTheEyeOptions(handleChangedOptions(sortedNewOptions, localStorageTheEyeName));
	};

	const shouldBlockBaseline = () => {
		return viewer.project.projectStartYear === null || viewer.project.projectEndYear === null;
	};

	const addProjectDates = () => {
		showModal({
			type: MODAL_TYPE.ADD_PROJECT_DATES,
			project: viewer.project,
			datesSetCallback: () => retry(),
		});
	};

	const handleToggleAllPhases = () => {
		toggleAllPhases(viewer.project.id, !collapseAll);
		setCollapseAll(!collapseAll);
	};

	const onPhaseCreate = res => {
		setPhaseSortOrder([res.createPhase.phase.node.id].concat(phaseSortOrder));
	};

	const getHeaderTitle = () => {
		const onboardingFlows = [
			{
				id: 'baseline-introduction',
				title: 'Baseline - Intro',
				description: null,
				contentId: '1604934773uOvw3403',
			},
		];
		return (
			<TopHeaderBarWrapper sidePadding={useThirdLevel ? 24 : 66}>
				<TopHeaderBar
					title={intl.formatMessage({id: 'project_section.baseline'})}
					content={[
						{
							id: 'onboarding-component',
							type: TopHeaderBar.TYPE.ONBOARDING,
							title: intl.formatMessage({id: 'baseline.onboarding_title'}),
							options: onboardingFlows,
							helpCenterLink: 'https://help.forecast.it/baseline',
						},
					]}
				/>
			</TopHeaderBarWrapper>
		);
	};

	const showAdjustBaselineModal = () => {
		showModal({
			type: MODAL_TYPE.BASELINE_ADJUST_MODAL,
			baselineTarget: viewer.project.baselineTarget,
			baselineTargetPrice: viewer.project.baselineTargetPrice,
			baselineAdjustPrice: viewer.project.baselineAdjustPrice,
			baselineAdjustPercentage: viewer.project.baselineAdjustPercentage,
			currency: viewer.project.rateCard ? viewer.project.rateCard.currency : viewer.company.currency,
			currencySymbol,
			projectId: viewer.project.id,
			onSave: adjustBaseline,
		});
	};

	const getHeader = () => {
		const leftContent = [];
		const rightContent = [];

		rightContent.push({
			type: ELEMENT_TYPE.BUTTON,
			text: intl.formatMessage({id: 'project_scoping.new-scope-group'}),
			style: BUTTON_STYLE.OUTLINE,
			color: BUTTON_COLOR.PURPLE,
			callback: () => createNewPhase(viewer.project.id, intl.formatMessage, onPhaseCreate),
			userpilot: 'new-milestone-button',
			dataCy: 'baseline-new-milestone-button',
			disabled: shouldBlockBaseline(),
		});
		if (viewer.project.budgetType === BUDGET_TYPE.FIXED_PRICE && hasFeatureFlag('baseline_adjustment', viewer.availableFeatureFlags)) {
			rightContent.push({
				type: ELEMENT_TYPE.BUTTON,
				text: intl.formatMessage({id: 'baseline.adjust_baseline_total'}),
				style: BUTTON_STYLE.OUTLINE_THICK,
				color: BUTTON_COLOR.LIGHTGREY,
				callback: showAdjustBaselineModal,
				userpilot: 'new-milestone-button',
				dataCy: 'baseline-new-milestone-button',
				disabled: shouldBlockBaseline(),
			});
		}

		if (!shouldBlockBaseline()) {
			const settings = {
				type: ELEMENT_TYPE.BUTTON,
				text: intl.formatMessage({id: 'settings.title'}),
				callback: () => history.push(`${location.pathname}/${SETTINGS_PATH}`),
				style: BUTTON_STYLE.OUTLINE_THICK,
				color: BUTTON_COLOR.LIGHTGREY,
				userpilot: 'baseline-settings-button',
				dataCy: 'baseline-settins-button-container',
			};
			rightContent.push(settings);
		}

		rightContent.push({
			type: ELEMENT_TYPE.COLLAPSE,
			collapsed: collapseAll,
			toggleCollapse: handleToggleAllPhases,
		});

		const theEye = {
			type: ELEMENT_TYPE.THE_EYE,
			options: theEyeOpts,
			onSelect: handleTheEyeOptionSelect,
			expandLeft: true,
			userpilot: 'eye-selector',
		};

		rightContent.push(theEye);

		if (hasFeatureFlag('baseline_csv_export', viewer.availableFeatureFlags)) {
			const projectId = viewer.project.id;

			const csv = {
				type: ELEMENT_TYPE.CSV,
				callback: () => exportData(theEyeOpts, null, {project: viewer.project}, {projectId}, 'CSV'),
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.LIGHTGREY,
				text: intl.formatMessage({id: 'common.export-csv'}),
				tooltipEnabled: true,
				tooltipProps: {
					autoPlace: true,
					grey: true,
					infoText: intl.formatMessage({id: 'common.export-csv'}),
				},
				disabled: shouldBlockBaseline(),
			};
			rightContent.push(csv);
		}

		if (useThirdLevel) {
			return buildThirdLevelNavHeader(leftContent, rightContent);
		}

		return <HeaderBar leftContent={leftContent} rightContent={rightContent} />;
	};

	return (
		<Switch>
			<Route exact path={path}>
				<InitialPlanPageWrapper data-cy={'baseline-page'}>
					<HeaderBarStyled>{getHeaderTitle()}</HeaderBarStyled>
					<HeaderBarStyled secondLevel>{getHeader()}</HeaderBarStyled>
					{shouldBlockBaseline() ? (
						<div data-cy="baseline-empty-state">
							<EmptyState pageName={EmptyState.EMPTY_STATE.BASELINE_NO_DATES} callback={addProjectDates} />
						</div>
					) : (
						<ContentContainer>
							<CustomScrollDiv autoHeight autoHeightMin={1} autoHeightMax={`calc(100vh - ${buyNowTime ? 217 : 177}px)`}>
								<StickySection thirdLevel={useThirdLevel} bottomSpacing>
									<HeaderSection project={viewer.project} currencySymbol={currencySymbol} />
								</StickySection>

								<PhasesSection
									expenseCategories={viewer.company.expenseCategories.edges}
									phaseSortOrder={phaseSortOrder}
									phases={viewer.project.phases.edges}
									intl={intl}
									project={viewer.project}
									roles={viewer.company.roles.edges}
									projectId={viewer.project.id}
									currency={viewer.company.currency}
									currencySymbol={currencySymbol}
									enabledColumns={enabledColumns}
									theEyeOptions={theEyeOpts}
									baselineTargetMinutes={viewer.project.baselineTargetMinutes}
									baselineTargetPrice={viewer.project.baselineTargetPrice}
									baselineWinChance={viewer.project.baselineWinChance}
									toggleCollapseAll={() => setShouldCollapseAll(viewer.project.id, collapseAll, setCollapseAll)}
									useThirdLevel={useThirdLevel}
								/>
							</CustomScrollDiv>
						</ContentContainer>
					)}
				</InitialPlanPageWrapper>
			</Route>
			<Route exact path={`${path}/${SETTINGS_PATH}`}>
				<Settings
					baselineMinutesPerDay={viewer.project.baselineEstimationMinutesPerDay}
					availableFeatureFlags={viewer.availableFeatureFlags}
					budgetType={viewer.project.budgetType}
					budget={viewer.project.budget}
					projectStage={viewer.project.status}
					project={viewer.project}
					company={viewer.company}
					currency={viewer.project.rateCard ? viewer.project.rateCard.currency : viewer.company.currency}
					baselineTargetMinutes={viewer.project.baselineTargetMinutes}
					baselineTargetPrice={viewer.project.baselineTargetPrice}
					baselineWinChance={viewer.project.baselineWinChance}
					salesforceOpportunity={viewer.project.salesforceOpportunity}
					selectedRateCardId={viewer.project.rateCard?.id}
					rateCards={viewer.company.rateCards.edges}
					taskCostCalculationType={viewer.project.taskCostCalculationType}
					projectDoneOrHalted={projectDoneOrHalted}
					unassignedTaskHourlyCost={viewer.project.unassignedTaskHourlyCost}
					baselineTarget={viewer.project.baselineTarget}
					useThirdLevel={useThirdLevel}
				/>
			</Route>
			<Route path="*">
				<Redirect to="/not-found" />
			</Route>
		</Switch>
	);
};

const InitialPlanPageQuery = graphql`
	query InitialPlanPage_Query($projectId: String) {
		viewer {
			actualPersonId
			component(name: "project_initial_plan")
			project(id: $projectId) {
				id
			}
			...InitialPlanPage_viewer @arguments(projectId: $projectId)
		}
	}
`;

export {InitialPlanPageQuery};

export default injectIntl(
	withRouter(
		withSocketHandling(
			createFragmentContainer(InitialPlanPage, {
				viewer: graphql`
					fragment InitialPlanPage_viewer on Viewer @argumentDefinitions(projectId: {type: "String"}) {
						id
						email
						backendId
						actualPersonId
						availableFeatureFlags {
							key
						}
						company {
							...RateCardDropdown_company
							currency
							roles(first: 1000000) @connection(key: "Company_roles", filters: []) {
								edges {
									...RoleDropdown_roles
									node {
										id
										name
									}
								}
							}
							rateCards {
								edges {
									...RateCardDropdown_rateCards
								}
							}
							modules {
								moduleType
							}
							expenseCategories(first: 1000000) @connection(key: "Company_expenseCategories", filters: []) {
								edges {
									node {
										id
										name
										disabled
									}
								}
							}
						}
						project(id: $projectId) {
							...HeaderSection_project
							...ProjectSection_project
							useBaseline
							budgetType
							budget
							id
							status
							companyProjectId
							projectColor
							name
							unassignedTaskHourlyCost
							taskCostCalculationType
							estimationUnit
							baselineWinChance
							baselineAdjustPrice
							baselineAdjustPercentage
							baselineTarget
							baselineEstimationMinutesPerDay
							baselineTargetPrice
							baselineTargetMinutes
							projectStartYear
							projectStartMonth
							projectStartDay
							projectEndYear
							projectEndMonth
							projectEndDay
							salesforceOpportunity
							rateCard {
								id
								currency
							}
							phases(first: 1000000) @connection(key: "Project_phases", filters: []) {
								edges {
									node {
										id
										startYear
										startMonth
										startDay
										deadlineYear
										deadlineMonth
										deadlineDay
									}
									...PhasesSection_phases
								}
							}
							invoices(first: 100000000) {
								edges {
									node {
										id
										status
									}
								}
							}
						}
					}
				`,
			})
		)
	)
);
